import React, { useState } from 'react';
import { format } from 'date-fns';
import { useLanguage } from '../context/LanguageContext';
import { ChevronLeft, ChevronRight, X, Sun, Moon, AlertTriangle, Check, Compass, Clock, Star, Info, Crown, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import FlyingStarDetail from './FlyingStarDetail';

// Direction usage explanations
const DIRECTION_USAGE = {
    happiness: {
        chinese: "喜神方位",
        english: "Joy God Direction",
        usage: "Face this direction when signing contracts, getting married, or starting important ventures. It attracts happiness, good fortune, and positive energy. Ideal for celebrations and joyful occasions."
    },
    wealth: {
        chinese: "財神方位", 
        english: "Wealth God Direction",
        usage: "Face this direction when conducting financial transactions, opening a business, or seeking prosperity. Position your cash register, safe, or workspace facing this direction to attract wealth."
    },
    conflict: {
        chinese: "沖煞方位",
        english: "Conflict Direction", 
        usage: "AVOID facing or traveling in this direction today. It brings obstacles, conflicts, and misfortune. Do not start important activities while facing this direction or travel far in this direction."
    },
    five_ghost: {
        chinese: "五鬼方位",
        english: "Five Ghost Direction",
        usage: "AVOID this direction for important matters. Associated with theft, loss, gossip, and supernatural disturbances. Do not place valuables or conduct business dealings in this direction."
    }
};

const getElementColor = (element) => {
    const colors = {
        water: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
        wood: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        fire: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        earth: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
        metal: 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200',
    };
    return colors[element] || 'bg-slate-100 text-slate-700';
};

const getRelationshipColor = (type) => {
    const colors = {
        'produce_in': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
        'same': 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
        'produce_out': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
        'control_in': 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
        'control_out': 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    };
    return colors[type] || 'bg-slate-100 text-slate-700';
};

// Calculate hour quality score for finding best timing
const calculateHourScore = (hour) => {
    let score = 0;
    
    // Element relationship scores
    const relationshipScores = {
        'produce_in': 30,    // Best - receiving energy
        'control_out': 20,   // Good - taking control
        'same': 10,          // Neutral
        'produce_out': -5,   // Draining
        'control_in': -15,   // Being controlled
    };
    score += relationshipScores[hour.element_relationship?.type] || 0;
    
    // Yellow Path (黄道) bonus
    if (hour.hour_star?.path === 'yellow') score += 25;
    
    // Auspicious hour star bonus
    if (hour.hour_star?.auspicious) score += 15;
    
    // Auspicious stars bonus
    if (hour.auspicious_stars?.length > 0) {
        score += hour.auspicious_stars.length * 8;
        // Extra bonus for special stars
        hour.auspicious_stars.forEach(star => {
            if (star.chinese?.includes('貴人') || star.english?.includes('Nobleman')) score += 10;
            if (star.chinese?.includes('財神') || star.english?.includes('Wealth')) score += 8;
            if (star.chinese?.includes('天德') || star.english?.includes('Heaven')) score += 8;
        });
    }
    
    // Inauspicious stars penalty
    if (hour.inauspicious_stars?.length > 0) {
        score -= hour.inauspicious_stars.length * 10;
        // Extra penalty for severe stars
        hour.inauspicious_stars.forEach(star => {
            if (star.chinese?.includes('日破') || star.english?.includes('Day Breaker')) score -= 20;
            if (star.chinese?.includes('日害') || star.english?.includes('Day Harm')) score -= 15;
        });
    }
    
    return score;
};

// Find best hours from the list
const findBestHours = (hours) => {
    if (!hours || hours.length === 0) return { best: null, secondBest: null, thirdBest: null };
    
    const scoredHours = hours.map(h => ({ ...h, score: calculateHourScore(h) }));
    const sorted = [...scoredHours].sort((a, b) => b.score - a.score);
    
    return {
        best: sorted[0],
        secondBest: sorted[1],
        thirdBest: sorted[2],
        scores: scoredHours
    };
};

function HourStarCell({ hour, language, onSelect, isSelected, rank }) {
    const isAuspicious = hour.hour_star?.auspicious;
    const isBestHour = rank === 1;
    const isSecondBest = rank === 2;
    const isThirdBest = rank === 3;
    
    let bgColor = isAuspicious 
        ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' 
        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
    
    // Override for best hours
    if (isBestHour) {
        bgColor = 'bg-gradient-to-br from-yellow-50 to-amber-100 dark:from-yellow-900/30 dark:to-amber-900/30 border-yellow-400 dark:border-yellow-600 border-2';
    } else if (isSecondBest) {
        bgColor = 'bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-800/50 dark:to-gray-800/50 border-slate-400 dark:border-slate-500 border-2';
    } else if (isThirdBest) {
        bgColor = 'bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-orange-300 dark:border-orange-700 border-2';
    }
    
    const selectedClass = isSelected ? 'ring-2 ring-primary ring-offset-1' : '';
    
    // Get rank stars
    const getRankStars = () => {
        if (isBestHour) return '⭐⭐⭐';
        if (isSecondBest) return '⭐⭐';
        if (isThirdBest) return '⭐';
        return null;
    };
    
    const rankStars = getRankStars();
    
    return (
        <div 
            className={`p-2 rounded-lg border ${bgColor} ${selectedClass} text-sm cursor-pointer hover:shadow-md transition-shadow relative`}
            onClick={() => onSelect(hour)}
        >
            {/* Rank badge */}
            {rankStars && (
                <div className={`absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap z-10 ${
                    isBestHour 
                        ? 'bg-yellow-400 text-yellow-900' 
                        : isSecondBest 
                            ? 'bg-slate-400 text-white' 
                            : 'bg-orange-400 text-orange-900'
                }`}>
                    {rankStars} {isBestHour ? 'BEST' : isSecondBest ? '2ND' : '3RD'}
                </div>
            )}
            
            <div className={`text-center mb-1 ${rankStars ? 'mt-2' : ''}`}>
                <div className="text-[12px] text-muted-foreground">{hour.time_range}</div>
                <div className="font-chinese text-lg font-bold">{hour.combined_chinese}</div>
                <div className="text-[12px]">{hour.branch?.animal}</div>
            </div>
            
            <div className={`text-center py-0.5 px-1 rounded text-[13px] mb-1 ${getRelationshipColor(hour.element_relationship?.type)}`}>
                {hour.element_relationship?.english}
            </div>
            
            <div className="text-center bg-purple-50 dark:bg-purple-900/30 rounded py-0.5 mb-1 border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors" data-testid="flying-star-display">
                <span className="text-[12px] font-mono font-bold text-purple-700 dark:text-purple-300">{hour.flying_stars?.display}</span>
            </div>
            
            <div className={`text-center py-1 rounded ${isAuspicious ? 'bg-emerald-100 dark:bg-emerald-800/40 text-emerald-700 dark:text-emerald-300' : 'bg-red-100 dark:bg-red-800/40 text-red-700 dark:text-red-300'}`}>
                <div className="font-chinese text-sm">{hour.hour_star?.chinese}</div>
                <div className="text-[12px]">{hour.hour_star?.english}</div>
                <div className="text-[13px] opacity-70">{hour.hour_star?.path === 'yellow' ? '黄道' : '黑道'}</div>
            </div>
            
            {hour.auspicious_stars?.length > 0 && (
                <div className="mt-1.5 space-y-0.5">
                    {hour.auspicious_stars.slice(0, 3).map((star, i) => (
                        <div key={i} className="text-[12px] text-emerald-600 dark:text-emerald-400 truncate leading-tight">
                            {star.chinese} {star.english}
                        </div>
                    ))}
                </div>
            )}
            
            {hour.inauspicious_stars?.length > 0 && (
                <div className="mt-1 space-y-0.5">
                    {hour.inauspicious_stars.slice(0, 2).map((star, i) => (
                        <div key={i} className="text-[12px] text-red-600 dark:text-red-400 truncate leading-tight">
                            {star.chinese} {star.english}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function HourDetailPanel({ hour, language }) {
    if (!hour) return null;
    
    const isAuspicious = hour.hour_star?.auspicious;
    const bgClass = isAuspicious 
        ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700' 
        : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700';
    
    return (
        <div className={`p-3 rounded-xl border ${bgClass} mb-3`}>
            <div className="flex items-start gap-3">
                <div className="text-center">
                    <div className="font-chinese text-2xl font-bold">{hour.hour_star?.chinese}</div>
                    <div className="text-sm font-medium">{hour.hour_star?.english}</div>
                    <div className="text-[12px] text-muted-foreground">{hour.hour_star?.pinyin}</div>
                    <Badge variant={isAuspicious ? "default" : "destructive"} className="mt-1 text-[13px]">
                        {hour.hour_star?.path === 'yellow' ? '黄道 Yellow' : '黑道 Black'}
                    </Badge>
                </div>
                <div className="flex-1">
                    <div className="text-sm font-medium mb-1">{hour.time_range} • {hour.combined_chinese} ({hour.branch?.animal})</div>
                    <p className="text-[13px] text-muted-foreground mb-2">{hour.hour_star?.meaning}</p>
                    
                    {hour.hour_star?.suitable?.length > 0 && (
                        <div className="mb-1.5">
                            <span className="text-[12px] font-semibold text-emerald-700 dark:text-emerald-300">Suitable: </span>
                            <span className="text-[12px]">{hour.hour_star.suitable.slice(0, 4).join(', ')}</span>
                        </div>
                    )}
                    
                    {hour.hour_star?.avoid?.length > 0 && (
                        <div>
                            <span className="text-[12px] font-semibold text-red-700 dark:text-red-300">Avoid: </span>
                            <span className="text-[12px]">{hour.hour_star.avoid.slice(0, 4).join(', ')}</span>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Flying Star Interpretation */}
            <FlyingStarDetail flyingStars={hour.flying_stars} />
        </div>
    );
}

function DateDetailModal({ data, date, onClose, onPrevDay, onNextDay, mode = 'beginner', hasProAccess = false }) {
    const { language } = useLanguage();
    const [selectedHour, setSelectedHour] = useState(null);
    const [expandedQuestion, setExpandedQuestion] = useState(null);
    const isPro = mode === 'practitioner';
    
    if (!data) return null;

    // Generate dynamic answers based on Tong Shu data
    const generateAnswers = () => {
        const rating = data.overall_rating || 'AVERAGE';
        const isYellowPath = data.path === 'yellow';
        const dayOfficer = data.day_officer?.english || '';
        const suitActivities = data.suit || [];
        const avoidActivities = data.avoid || [];
        const auspiciousStars = data.auspicious_stars || [];
        const inauspiciousStars = data.inauspicious_stars || [];
        const clash = data.clash;
        const dongGong = data.dong_gong;
        
        // Rating descriptions
        const ratingDesc = {
            'BEST': { en: 'Excellent', zh: '大吉', emoji: '🌟' },
            'GOOD': { en: 'Favorable', zh: '吉', emoji: '✨' },
            'AVERAGE': { en: 'Neutral', zh: '平', emoji: '➖' },
            'POOR': { en: 'Challenging', zh: '凶', emoji: '⚠️' }
        };
        
        const rd = ratingDesc[rating] || ratingDesc['AVERAGE'];
        
        return [
            {
                q: { en: 'What activities are suitable for this day?', zh: '今日適宜做什麼？' },
                a: suitActivities.length > 0 
                    ? `${rd.emoji} This day is suitable for: ${suitActivities.slice(0, 6).join(', ')}. ${isYellowPath ? 'Being a Yellow Path (黃道) day adds extra auspiciousness.' : ''}`
                    : `${rd.emoji} General activities are okay. Check the day officer (${dayOfficer}) for specific guidance.`,
                category: 'activities'
            },
            {
                q: { en: 'What should I pay attention to on this day?', zh: '今日需要注意什麼？' },
                a: (() => {
                    let warnings = [];
                    if (avoidActivities.length > 0) warnings.push(`Avoid: ${avoidActivities.slice(0, 4).join(', ')}`);
                    if (clash) warnings.push(`Clash with ${clash.animal?.english || clash.branch?.animal || 'certain zodiac'} - people born in this year should be extra cautious`);
                    if (inauspiciousStars.length > 0) warnings.push(`Watch for: ${inauspiciousStars.slice(0, 2).map(s => s.english || s.chinese).join(', ')}`);
                    if (!isYellowPath) warnings.push('Black Path (黑道) day - exercise more caution');
                    return warnings.length > 0 ? `⚠️ ${warnings.join('. ')}.` : '✅ No major concerns for this day. Proceed with normal activities.';
                })(),
                category: 'caution'
            },
            {
                q: { en: 'How is the fortune for this day?', zh: '今日運勢如何？' },
                a: `${rd.emoji} Overall: ${rd.en} (${rd.zh}). ${
                    dongGong?.score ? `Dong Gong Score: ${dongGong.score}/100. ` : ''
                }${
                    auspiciousStars.length > 0 
                        ? `Blessed by: ${auspiciousStars.slice(0, 3).map(s => s.english || s.chinese).join(', ')}.` 
                        : ''
                } Day Officer "${dayOfficer}" ${
                    ['Establish', 'Full', 'Balance', 'Success'].includes(dayOfficer) 
                        ? 'is favorable for initiating matters.' 
                        : ['Remove', 'Destruction', 'Close'].includes(dayOfficer)
                            ? 'favors completion and clearing.'
                            : 'brings neutral energy.'
                }`,
                category: 'fortune'
            },
            {
                q: { en: 'Is this day suitable for traveling?', zh: '今日適合出行嗎？' },
                a: (() => {
                    const travelSuit = suitActivities.some(a => a.toLowerCase().includes('travel') || a.toLowerCase().includes('journey') || a.toLowerCase().includes('move'));
                    const travelAvoid = avoidActivities.some(a => a.toLowerCase().includes('travel') || a.toLowerCase().includes('journey'));
                    if (travelAvoid) return '⚠️ Not recommended for travel today. Consider postponing your journey if possible.';
                    if (travelSuit) return '✅ Great day for traveling! The energy supports safe and smooth journeys.';
                    if (rating === 'BEST' || rating === 'GOOD') return '✨ Generally favorable for travel. No major obstacles indicated.';
                    if (clash) return `⚠️ Travel with caution, especially if you are a ${clash.animal?.english || 'clashing zodiac'}. Check directions.`;
                    return '➖ Neutral for travel. No strong indications either way. Proceed with normal precautions.';
                })(),
                category: 'travel'
            },
            {
                q: { en: 'Is this day good for signing contracts or opening business?', zh: '今日適合簽約開業嗎？' },
                a: (() => {
                    const bizSuit = suitActivities.some(a => 
                        a.toLowerCase().includes('sign') || 
                        a.toLowerCase().includes('contract') || 
                        a.toLowerCase().includes('open') ||
                        a.toLowerCase().includes('business') ||
                        a.toLowerCase().includes('trade')
                    );
                    const bizAvoid = avoidActivities.some(a => 
                        a.toLowerCase().includes('sign') || 
                        a.toLowerCase().includes('open') ||
                        a.toLowerCase().includes('business')
                    );
                    if (bizAvoid) return '⚠️ Not ideal for business dealings or contracts today. Important agreements should wait.';
                    if (bizSuit && isYellowPath) return '🌟 Excellent! Yellow Path day with favorable energy for business, contracts, and new ventures.';
                    if (bizSuit) return '✅ Good day for business activities and signing agreements.';
                    if (rating === 'BEST' && isYellowPath) return '✨ Very favorable for business dealings. Strong auspicious energy present.';
                    if (rating === 'POOR' || !isYellowPath) return '⚠️ Consider postponing major business decisions. Energy is not optimal.';
                    return '➖ Average day for business. No strong support or opposition. Use your judgment.';
                })(),
                category: 'business'
            },
            {
                q: { en: 'How is the wealth luck on this day?', zh: '今日財運如何？' },
                a: (() => {
                    const wealthStars = auspiciousStars.filter(s => 
                        (s.english && s.english.toLowerCase().includes('wealth')) ||
                        (s.chinese && (s.chinese.includes('財') || s.chinese.includes('祿')))
                    );
                    const hasWealth = wealthStars.length > 0;
                    const wealthDirection = data.directions?.wealth;
                    
                    let answer = '';
                    if (hasWealth) answer = `💰 Wealth luck is strong! Blessed by: ${wealthStars.map(s => s.english || s.chinese).join(', ')}. `;
                    else if (rating === 'BEST') answer = '✨ Good wealth potential today. ';
                    else if (rating === 'POOR') answer = '⚠️ Wealth luck is weak. Avoid risky investments. ';
                    else answer = '➖ Average wealth luck. ';
                    
                    if (wealthDirection) {
                        answer += `Wealth God Direction: ${wealthDirection.direction} (${wealthDirection.chinese || ''}). Face this direction for financial activities.`;
                    }
                    return answer;
                })(),
                category: 'wealth'
            }
        ];
    };
    
    const suggestedQuestions = generateAnswers();

    const dongGongBg = data.dong_gong?.rating === 'auspicious' 
        ? 'bg-emerald-50 dark:bg-emerald-900/30' 
        : data.dong_gong?.rating === 'inauspicious' 
            ? 'bg-red-50 dark:bg-red-900/30' 
            : 'bg-slate-50 dark:bg-slate-800';

    const dayOfficerColor = data.day_officer?.auspicious 
        ? 'text-emerald-600 dark:text-emerald-400' 
        : 'text-red-600 dark:text-red-400';

    const pathBg = data.path?.is_yellow_path 
        ? 'bg-yellow-50 dark:bg-yellow-900/30' 
        : 'bg-slate-100 dark:bg-slate-800';

    return (
        <div 
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2" 
            onClick={onClose}
        >
            <div 
                className="bg-background rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200 relative"
                onClick={e => e.stopPropagation()}
                data-testid="date-detail-modal"
            >
                {/* Close button - visible on mobile */}
                <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={onClose} 
                    className="absolute top-2 right-2 z-10 text-primary-foreground hover:bg-white/20 h-8 w-8 md:hidden" 
                    data-testid="modal-close-x"
                >
                    <X className="w-5 h-5" />
                </Button>
                
                <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground p-3">
                    <div className="flex items-center justify-between mb-2">
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={onPrevDay} 
                            className="text-primary-foreground hover:bg-white/20 h-8 w-8" 
                            data-testid="modal-prev-day"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <div className="text-center">
                            <div className="text-sm opacity-80">{format(date, 'EEEE')}</div>
                            <div className="text-xl font-serif font-bold">{format(date, 'MMMM d, yyyy')}</div>
                        </div>
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={onNextDay} 
                            className="text-primary-foreground hover:bg-white/20 h-8 w-8" 
                            data-testid="modal-next-day"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                        <div className="text-center bg-white/10 rounded-lg p-1.5">
                            <div className="text-[13px] opacity-70">{language === 'zh' ? '年柱' : 'Year'}</div>
                            <div className="font-chinese text-lg font-bold">{data.pillars?.year?.combined_chinese}</div>
                        </div>
                        <div className="text-center bg-white/10 rounded-lg p-1.5">
                            <div className="text-[13px] opacity-70">{language === 'zh' ? '月柱' : 'Month'}</div>
                            <div className="font-chinese text-lg font-bold">{data.pillars?.month?.combined_chinese}</div>
                        </div>
                        <div className="text-center bg-white/10 rounded-lg p-1.5">
                            <div className="text-[13px] opacity-70">{language === 'zh' ? '日柱' : 'Day'}</div>
                            <div className="font-chinese text-lg font-bold">{data.pillars?.day?.combined_chinese}</div>
                        </div>
                    </div>
                </div>
                
                <ScrollArea className="h-[calc(95vh-140px)]" data-testid="detail-modal-content">
                    <div className="p-3 space-y-3">
                        <TooltipProvider>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className={`p-2 rounded-xl text-center cursor-help hover:ring-2 hover:ring-primary/50 transition-all ${dongGongBg}`}>
                                        <div className="font-chinese text-xl font-bold">{data.dong_gong?.chinese}</div>
                                        <div className="text-[12px] text-muted-foreground">{data.dong_gong?.english}</div>
                                        <div className="text-[13px] mt-0.5 opacity-70">
                                            {language === 'zh' ? '董公' : 'Dong Gong'}
                                        </div>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-[300px] p-3">
                                    <p className="font-semibold text-primary mb-1">{data.dong_gong?.chinese} {data.dong_gong?.english}</p>
                                    <p className="text-sm">{data.dong_gong?.description || `This day is rated as "${data.dong_gong?.english}" by the Dong Gong system.`}</p>
                                </TooltipContent>
                            </Tooltip>
                            
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className={`p-2 rounded-xl text-center cursor-help hover:ring-2 hover:ring-primary/50 transition-all ${pathBg}`}>
                                        {data.path?.is_yellow_path 
                                            ? <Sun className="w-5 h-5 text-yellow-500 mx-auto" />
                                            : <Moon className="w-5 h-5 text-slate-400 mx-auto" />
                                        }
                                        <div className="font-chinese text-sm font-bold">{data.path?.chinese}</div>
                                        <div className="text-[13px] text-muted-foreground">
                                            {data.path?.quality}
                                        </div>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-[300px] p-3">
                                    <p className="font-semibold text-primary mb-1">{data.path?.chinese} - {data.path?.english}</p>
                                    <p className="text-sm">{data.path?.description}</p>
                                </TooltipContent>
                            </Tooltip>
                            
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className="p-2 rounded-xl text-center bg-muted/50 cursor-help hover:ring-2 hover:ring-primary/50 transition-all">
                                        <div className={`font-chinese text-xl font-bold ${dayOfficerColor}`}>
                                            {data.day_officer?.chinese}
                                        </div>
                                        <div className="text-[12px]">{data.day_officer?.english}</div>
                                        <div className="text-[13px] text-muted-foreground mt-0.5">
                                            {language === 'zh' ? '建除' : 'Day Officer'}
                                        </div>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-[300px] p-3">
                                    <p className="font-semibold text-primary mb-1">{data.day_officer?.chinese} {data.day_officer?.english}</p>
                                    <p className="text-sm">{data.day_officer?.description}</p>
                                </TooltipContent>
                            </Tooltip>
                            
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className={`p-2 rounded-xl text-center cursor-help hover:ring-2 hover:ring-primary/50 transition-all ${getElementColor(data.lunar_mansion?.element)}`}>
                                        <div className="font-chinese text-xl font-bold">{data.lunar_mansion?.chinese}</div>
                                        <div className="text-[12px]">{data.lunar_mansion?.english}</div>
                                        <div className="text-[13px] opacity-70 mt-0.5">
                                            {language === 'zh' ? '宿' : 'Mansion'}
                                        </div>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-[300px] p-3">
                                    <p className="font-semibold text-primary mb-1">{data.lunar_mansion?.chinese} {data.lunar_mansion?.english} ({data.lunar_mansion?.animal})</p>
                                    <p className="text-sm">{data.lunar_mansion?.auspicious ? '✓ Auspicious mansion' : '⚠ Use caution'}. Element: {data.lunar_mansion?.element}. {data.lunar_mansion?.suitable?.length > 0 ? `Good for: ${data.lunar_mansion.suitable.join(', ')}.` : ''}</p>
                                </TooltipContent>
                            </Tooltip>
                        </div>
                        </TooltipProvider>
                        
                        {data.path?.description && (
                            <div className={`p-2 rounded-lg ${pathBg} text-[12px]`}>
                                <Info className="w-3 h-3 inline mr-1" />
                                <span className="font-semibold">{data.path?.chinese} {data.path?.english}:</span> {data.path?.description}
                            </div>
                        )}
                        
                        <div className="grid md:grid-cols-2 gap-2">
                            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-2 border border-emerald-200 dark:border-emerald-800">
                                <h3 className="font-semibold text-emerald-800 dark:text-emerald-200 flex items-center gap-1.5 mb-1.5 text-sm">
                                    <Star className="w-3 h-3" />
                                    {language === 'zh' ? '日吉星' : 'Daily Auspicious Stars'}
                                </h3>
                                <div className="flex flex-wrap gap-1">
                                    {data.daily_stars?.auspicious?.map((star, i) => (
                                        <Badge key={i} variant="secondary" className="bg-white dark:bg-emerald-800/50 text-[13px] py-0">
                                            {star.chinese} {star.english}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-2 border border-red-200 dark:border-red-800">
                                <h3 className="font-semibold text-red-800 dark:text-red-200 flex items-center gap-1.5 mb-1.5 text-sm">
                                    <AlertTriangle className="w-3 h-3" />
                                    {language === 'zh' ? '日凶星' : 'Daily Inauspicious Stars'}
                                </h3>
                                <div className="flex flex-wrap gap-1">
                                    {data.daily_stars?.inauspicious?.map((star, i) => (
                                        <Badge key={i} variant="secondary" className="bg-white dark:bg-red-800/50 text-[13px] py-0">
                                            {star.chinese} {star.english}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        {/* Hour Stars - Only show in Pro mode */}
                        {isPro && (
                        <div className="bg-card rounded-xl p-2 border">
                            <h3 className="font-semibold mb-2 flex items-center gap-1.5 text-sm">
                                <Clock className="w-3 h-3" />
                                {language === 'zh' ? '時辰吉凶 Hour Stars' : 'Hour Stars 時辰吉凶'}
                                <span className="text-[12px] text-muted-foreground font-normal ml-2">(Click an hour for details)</span>
                                
                                {/* Element Relationship Help Tooltip */}
                                <TooltipProvider>
                                    <Tooltip delayDuration={0}>
                                        <TooltipTrigger asChild>
                                            <button className="ml-2 w-5 h-5 rounded-full bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 text-xs font-bold transition-colors">
                                                ?
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" className="w-80 p-3 bg-white dark:bg-slate-800 shadow-xl border">
                                            <div className="space-y-2">
                                                <div className="font-semibold text-sm border-b pb-1 mb-2">
                                                    {language === 'zh' ? '五行關係說明' : 'Element Relationships'}
                                                </div>
                                                <div className="flex items-start gap-2 text-xs">
                                                    <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-medium whitespace-nowrap">Produce In 生入</span>
                                                    <span className="text-muted-foreground">✅ Hour feeds you energy (favorable)</span>
                                                </div>
                                                <div className="flex items-start gap-2 text-xs">
                                                    <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium whitespace-nowrap">Control Out 克出</span>
                                                    <span className="text-muted-foreground">✅ You take charge (action-oriented)</span>
                                                </div>
                                                <div className="flex items-start gap-2 text-xs">
                                                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium whitespace-nowrap">Same 比和</span>
                                                    <span className="text-muted-foreground">➖ Peer energy (neutral)</span>
                                                </div>
                                                <div className="flex items-start gap-2 text-xs">
                                                    <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium whitespace-nowrap">Produce Out 生出</span>
                                                    <span className="text-muted-foreground">⚠️ Giving away energy (draining)</span>
                                                </div>
                                                <div className="flex items-start gap-2 text-xs">
                                                    <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium whitespace-nowrap">Control In 克入</span>
                                                    <span className="text-muted-foreground">⚠️ Being controlled (challenging)</span>
                                                </div>
                                                <div className="border-t pt-2 mt-2 text-[11px] text-muted-foreground">
                                                    <strong>Path:</strong> 黃道 Yellow = Auspicious | 黑道 Black = Inauspicious
                                                </div>
                                            </div>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </h3>
                            
                            {selectedHour && (
                                <HourDetailPanel hour={selectedHour} language={language} />
                            )}
                            
                            {/* Best Hours Legend */}
                            {data.hours && data.hours.length > 0 && (
                                <div className="flex flex-wrap items-center gap-3 mb-2 text-xs">
                                    <span className="text-muted-foreground">Best Timing:</span>
                                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full font-medium">⭐⭐⭐ BEST</span>
                                    <span className="px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full font-medium">⭐⭐ 2ND</span>
                                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-medium">⭐ 3RD</span>
                                </div>
                            )}
                            
                            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-1.5">
                                {(() => {
                                    const bestHours = findBestHours(data.hours);
                                    return data.hours?.map((hour, index) => {
                                        let rank = 0;
                                        if (bestHours.best && hour.branch_index === bestHours.best.branch_index) rank = 1;
                                        else if (bestHours.secondBest && hour.branch_index === bestHours.secondBest.branch_index) rank = 2;
                                        else if (bestHours.thirdBest && hour.branch_index === bestHours.thirdBest.branch_index) rank = 3;
                                        
                                        return (
                                            <HourStarCell 
                                                key={index} 
                                                hour={hour} 
                                                language={language}
                                                onSelect={setSelectedHour}
                                                isSelected={selectedHour?.branch_index === hour.branch_index}
                                                rank={rank}
                                            />
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                        )}
                        
                        <div className="grid md:grid-cols-2 gap-2">
                            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-2 border border-emerald-200 dark:border-emerald-800">
                                <h3 className="font-semibold text-emerald-800 dark:text-emerald-200 flex items-center gap-1.5 mb-1.5 text-sm">
                                    <Check className="w-3 h-3" />
                                    {language === 'zh' ? '宜 Suit' : 'Suit 宜'}
                                </h3>
                                <div className="flex flex-wrap gap-1">
                                    {data.activities?.recommended?.slice(0, 10).map((act, i) => (
                                        <Badge key={i} variant="secondary" className="bg-white dark:bg-emerald-800/50 text-[13px] capitalize py-0">{act}</Badge>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-2 border border-red-200 dark:border-red-800">
                                <h3 className="font-semibold text-red-800 dark:text-red-200 flex items-center gap-1.5 mb-1.5 text-sm">
                                    <X className="w-3 h-3" />
                                    {language === 'zh' ? '忌 Avoid' : 'Avoid 忌'}
                                </h3>
                                <div className="flex flex-wrap gap-1">
                                    {data.activities?.avoid?.slice(0, 10).map((act, i) => (
                                        <Badge key={i} variant="secondary" className="bg-white dark:bg-red-800/50 text-[13px] capitalize py-0">{act}</Badge>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-2 border border-amber-200 dark:border-amber-800">
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="w-3 h-3 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <h3 className="font-semibold text-amber-800 dark:text-amber-200 mb-1.5 text-sm">
                                        {language === 'zh' ? '冲煞' : 'Clash & Conflict'}
                                    </h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 text-[12px]">
                                        <div className="bg-white/50 dark:bg-black/20 rounded p-1.5">
                                            <span className="text-muted-foreground block text-[13px]">{language === 'zh' ? '冲' : 'Clash'}</span>
                                            <span className="font-chinese">{data.clash?.clash?.branch?.chinese}</span>
                                            <span className="ml-1 text-[13px]">{data.clash?.clash?.branch?.english}</span>
                                        </div>
                                        <div className="bg-white/50 dark:bg-black/20 rounded p-1.5">
                                            <span className="text-muted-foreground block text-[13px]">{language === 'zh' ? '破' : 'Break'}</span>
                                            <span className="font-chinese">{data.clash?.break?.branch?.chinese}</span>
                                            <span className="ml-1 text-[13px]">{data.clash?.break?.branch?.english}</span>
                                        </div>
                                        <div className="bg-white/50 dark:bg-black/20 rounded p-1.5">
                                            <span className="text-muted-foreground block text-[13px]">{language === 'zh' ? '害' : 'Harm'}</span>
                                            <span className="font-chinese">{data.clash?.harm?.branch?.chinese}</span>
                                            <span className="ml-1 text-[13px]">{data.clash?.harm?.branch?.english}</span>
                                        </div>
                                        <div className="bg-white/50 dark:bg-black/20 rounded p-1.5">
                                            <span className="text-muted-foreground block text-[13px]">{language === 'zh' ? '刑' : 'Punish'}</span>
                                            <span className="font-chinese">{data.clash?.punishment?.branch?.chinese}</span>
                                            <span className="ml-1 text-[13px]">{data.clash?.punishment?.branch?.english}</span>
                                        </div>
                                    </div>
                                    {data.personal_clash?.is_clash_day && (
                                        <div className="mt-1.5 p-1.5 bg-purple-100 dark:bg-purple-900/40 rounded-lg text-purple-800 dark:text-purple-200 text-[12px] font-medium">
                                            ⚠️ {data.personal_clash.warning}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {/* Directions & Na Yin - Only show in Pro mode */}
                        {isPro && (
                        <TooltipProvider>
                        <div className="grid md:grid-cols-2 gap-2">
                            <div className="bg-card rounded-xl p-2 border">
                                <h3 className="font-semibold mb-1.5 flex items-center gap-1.5 text-sm">
                                    <Compass className="w-3 h-3" />
                                    {language === 'zh' ? '方位' : 'Directions'}
                                    <span className="text-[11px] text-muted-foreground font-normal">(click for usage)</span>
                                </h3>
                                <div className="grid grid-cols-2 gap-1.5">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="text-center p-1.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg cursor-help hover:ring-2 hover:ring-emerald-300 transition-all">
                                                <div className="text-[13px] text-muted-foreground">
                                                    {language === 'zh' ? '喜神' : 'Joy'}
                                                </div>
                                                <div className="font-chinese text-base">{data.directions?.happiness?.chinese}</div>
                                                <div className="text-[13px]">{data.directions?.happiness?.english}</div>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" className="max-w-[280px] p-3">
                                            <p className="font-semibold text-emerald-600 mb-1">{DIRECTION_USAGE.happiness.english}</p>
                                            <p className="text-sm">{DIRECTION_USAGE.happiness.usage}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="text-center p-1.5 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg cursor-help hover:ring-2 hover:ring-yellow-300 transition-all">
                                                <div className="text-[13px] text-muted-foreground">
                                                    {language === 'zh' ? '财神' : 'Wealth'}
                                                </div>
                                                <div className="font-chinese text-base">{data.directions?.wealth?.chinese}</div>
                                                <div className="text-[13px]">{data.directions?.wealth?.english}</div>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" className="max-w-[280px] p-3">
                                            <p className="font-semibold text-yellow-600 mb-1">{DIRECTION_USAGE.wealth.english}</p>
                                            <p className="text-sm">{DIRECTION_USAGE.wealth.usage}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="text-center p-1.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg cursor-help hover:ring-2 hover:ring-amber-300 transition-all">
                                                <div className="text-[13px] text-muted-foreground">
                                                    {language === 'zh' ? '冲煞' : 'Conflict'}
                                                </div>
                                                <div className="font-chinese text-base">{data.directions?.conflict?.chinese}</div>
                                                <div className="text-[13px]">{data.directions?.conflict?.english}</div>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" className="max-w-[280px] p-3">
                                            <p className="font-semibold text-amber-600 mb-1">⚠️ {DIRECTION_USAGE.conflict.english}</p>
                                            <p className="text-sm">{DIRECTION_USAGE.conflict.usage}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="text-center p-1.5 bg-purple-50 dark:bg-purple-900/20 rounded-lg cursor-help hover:ring-2 hover:ring-purple-300 transition-all">
                                                <div className="text-[13px] text-muted-foreground">
                                                    {language === 'zh' ? '五鬼' : 'Ghost'}
                                                </div>
                                                <div className="font-chinese text-base">{data.directions?.five_ghost?.chinese}</div>
                                                <div className="text-[13px]">{data.directions?.five_ghost?.english}</div>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" className="max-w-[280px] p-3">
                                            <p className="font-semibold text-purple-600 mb-1">⚠️ {DIRECTION_USAGE.five_ghost.english}</p>
                                            <p className="text-sm">{DIRECTION_USAGE.five_ghost.usage}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                            </div>
                            
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className={`rounded-xl p-2 cursor-help hover:ring-2 hover:ring-primary/50 transition-all ${getElementColor(data.na_yin?.element)}`}>
                                        <h3 className="font-semibold mb-1 text-sm">
                                            {language === 'zh' ? '纳音' : 'Na Yin'}
                                        </h3>
                                        <div className="flex items-center gap-2">
                                            <div className="font-chinese text-xl font-bold">{data.na_yin?.chinese}</div>
                                            <div>
                                                <div className="text-sm font-medium">{data.na_yin?.pinyin}</div>
                                                <div className="text-[12px] opacity-80">{data.na_yin?.english}</div>
                                            </div>
                                        </div>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-[320px] p-3">
                                    <p className="font-semibold text-primary mb-1">{data.na_yin?.chinese} - {data.na_yin?.english}</p>
                                    <p className="text-sm">{data.na_yin?.description || "One of the 60 Jia Zi Na Yin sound elements that describes the nature and characteristics of this day's energy."}</p>
                                </TooltipContent>
                            </Tooltip>
                        </div>
                        </TooltipProvider>
                        )}
                        
                        {/* Suggested Questions Section - Bottom */}
                        {isPro && (
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-3 border border-blue-200 dark:border-blue-800">
                            <h3 className="font-semibold text-blue-800 dark:text-blue-200 flex items-center gap-1.5 mb-2 text-sm">
                                <Info className="w-3.5 h-3.5" />
                                {language === 'zh' ? '常見問題 Suggested Questions' : 'Suggested Questions 常見問題'}
                            </h3>
                            <div className="space-y-2">
                                {suggestedQuestions.map((item, idx) => (
                                    <div key={idx} className="bg-white/80 dark:bg-slate-800/80 rounded-lg overflow-hidden border border-blue-100 dark:border-blue-800" data-testid={`suggested-question-${idx}`}>
                                        <button
                                            onClick={() => setExpandedQuestion(expandedQuestion === idx ? null : idx)}
                                            className="w-full px-3 py-2 text-left flex items-center justify-between hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                                            data-testid={`question-toggle-${idx}`}
                                        >
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                                                {language === 'zh' ? item.q.zh : item.q.en}
                                            </span>
                                            <span className={`text-blue-500 transition-transform duration-200 ${expandedQuestion === idx ? 'rotate-180' : ''}`}>
                                                ▼
                                            </span>
                                        </button>
                                        {expandedQuestion === idx && (
                                            <div className="px-3 py-2 bg-blue-50/50 dark:bg-blue-900/20 border-t border-blue-100 dark:border-blue-800" data-testid={`answer-${idx}`}>
                                                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                                    {item.a}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        )}
                        
                        {/* PRO Upgrade Prompt - Show for non-PRO users */}
                        {!hasProAccess && (
                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-4 border-2 border-amber-300 dark:border-amber-700" data-testid="upgrade-prompt">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-800 flex items-center justify-center flex-shrink-0">
                                    <Crown className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-bold text-amber-800 dark:text-amber-200 flex items-center gap-2">
                                        <Lock className="w-4 h-4" />
                                        {language === 'zh' ? '解鎖完整詳情' : 'Unlock Full Details'}
                                    </h3>
                                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1 mb-3">
                                        {language === 'zh' 
                                            ? '升級至 PRO 以獲取時辰吉凶、方位指南、納音五行、智能問答等高級功能。' 
                                            : 'Upgrade to PRO for Hour Stars, Direction Guide, NaYin Elements, AI Q&A, and more advanced features.'}
                                    </p>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        <span className="text-xs bg-amber-200/50 dark:bg-amber-800/50 text-amber-800 dark:text-amber-200 px-2 py-1 rounded-full flex items-center gap-1">
                                            <Clock className="w-3 h-3" /> {language === 'zh' ? '時辰吉凶' : 'Hour Stars'}
                                        </span>
                                        <span className="text-xs bg-amber-200/50 dark:bg-amber-800/50 text-amber-800 dark:text-amber-200 px-2 py-1 rounded-full flex items-center gap-1">
                                            <Compass className="w-3 h-3" /> {language === 'zh' ? '方位指南' : 'Directions'}
                                        </span>
                                        <span className="text-xs bg-amber-200/50 dark:bg-amber-800/50 text-amber-800 dark:text-amber-200 px-2 py-1 rounded-full flex items-center gap-1">
                                            <Star className="w-3 h-3" /> {language === 'zh' ? '納音五行' : 'NaYin'}
                                        </span>
                                        <span className="text-xs bg-amber-200/50 dark:bg-amber-800/50 text-amber-800 dark:text-amber-200 px-2 py-1 rounded-full flex items-center gap-1">
                                            <Info className="w-3 h-3" /> {language === 'zh' ? '智能問答' : 'AI Q&A'}
                                        </span>
                                    </div>
                                    <a 
                                        href="/subscription" 
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors text-sm"
                                        data-testid="modal-upgrade-btn"
                                    >
                                        <Crown className="w-4 h-4" />
                                        {language === 'zh' ? '升級至 PRO' : 'Upgrade to PRO'}
                                        <span className="text-xs bg-white/20 px-2 py-0.5 rounded">$7 Trial</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                        )}
                    </div>
                </ScrollArea>
                
                <div className="border-t p-2 flex justify-end bg-muted/30">
                    <Button onClick={onClose} variant="outline" size="sm" data-testid="modal-close-btn">
                        <X className="w-3 h-3 mr-1.5" />
                        {language === 'zh' ? '关闭' : 'Close'}
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default DateDetailModal;
